import { useAuth } from '../context/AuthContext'
import { UserType } from '../types'

type Action = 'create' | 'read' | 'update' | 'delete' | 'export' | 'approve' | 'admin'
type Resource = 'projects' | 'resources' | 'customers' | 'allocations' | 'reports' | 'settings' | 'users'

const USER_TYPE_PERMISSIONS: Record<UserType, Action[]> = {
  owner: ['create', 'read', 'update', 'delete', 'export', 'approve', 'admin'],
  admin: ['create', 'read', 'update', 'delete', 'export', 'approve', 'admin'],
  manager: ['create', 'read', 'update', 'delete', 'export', 'approve'],
  contributor: ['create', 'read', 'update', 'export'],
  viewer: ['read', 'export'],
  resource: ['read'],
}

export function usePermissions() {
  const { orgMember } = useAuth()
  const userType = (orgMember?.user_type || 'viewer') as UserType
  const role = orgMember?.role || 'viewer'

  const can = (action: Action, resource?: Resource, recordOwnerId?: string): boolean => {
    const allowedActions = USER_TYPE_PERMISSIONS[userType] || []

    if (userType === 'contributor' && (action === 'update' || action === 'delete')) {
      if (recordOwnerId && orgMember?.user_id !== recordOwnerId) {
        return false
      }
    }

    if (userType === 'viewer' && action === 'delete') {
      return false
    }

    return allowedActions.includes(action)
  }

  const canCreate = (resource?: Resource) => can('create', resource)
  const canRead = (resource?: Resource) => can('read', resource)
  const canUpdate = (resource?: Resource, recordOwnerId?: string) => can('update', resource, recordOwnerId)
  const canDelete = (resource?: Resource, recordOwnerId?: string) => can('delete', resource, recordOwnerId)
  const canExport = (resource?: Resource) => can('export', resource)
  const canApprove = (resource?: Resource) => can('approve', resource)
  const canAccessAdmin = () => can('admin')

  const isOwner = userType === 'owner'
  const isAdmin = userType === 'admin' || userType === 'owner'
  const isManager = userType === 'manager' || userType === 'admin' || userType === 'owner'
  const isContributor = userType === 'contributor'
  const isViewer = userType === 'viewer'
  const isResource = userType === 'resource'

  const canManageUsers = userType === 'owner' || userType === 'admin'
  const canManageDepartments = userType === 'owner' || userType === 'admin'
  const canManageSubscription = userType === 'owner'

  return {
    can,
    canCreate,
    canRead,
    canUpdate,
    canDelete,
    canExport,
    canApprove,
    canAccessAdmin,
    canManageUsers,
    canManageDepartments,
    canManageSubscription,
    isOwner,
    isAdmin,
    isManager,
    isContributor,
    isViewer,
    isResource,
    userType,
    role,
  }
}
